import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  Link,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { DndContext, DragEndEvent, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useNavigate, useParams } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';

import { BackNavIconVariant } from '_lex/_atoms/NavigationHeader/index';
import { TakeoverLayout } from '_lex/_layouts/TakeoverLayout';
import { translate as t } from 'hooks/useTranslations';
import { neutral } from '_lex/_foundations/phxColors';
import errorHelper from 'helpers/errorHelper';
import type { ServerErrorResponse } from 'data_sources/constants';
import { AppContext } from 'views/App/context';

import {
  EditWorkflowStepForm,
  readOnlyDashedTextFieldSx,
  SortableWorkflowStepRow,
  useDiscardWorkflowLeaveConfirm,
  workflowStepListFixedEndSpacerSx,
  workflowStepListRowSx,
} from '../_components/WorkflowBuilder';
import {
  createWorkflowMiddleStep,
  mapApiStepToMiddleStep,
  mapMiddleStepToApiStep,
  removeMiddleStepByKey,
  reorderMiddleSteps,
  updateMiddleStep,
} from '../_helpers/workflowStepHelpers';
import type { WorkflowMiddleStep, WorkflowStepLocalKey } from '../_types/workflowBuilder';
import useReviewWorkflow from '../_data_sources/useReviewWorkflow';
import useUpdateReviewWorkflow from '../_data_sources/useUpdateReviewWorkflow';

const stepStackSpacing = 2;

function snapshotState(steps: WorkflowMiddleStep[], defaultWorkflow: boolean): string {
  return JSON.stringify({ steps, defaultWorkflow });
}

const ReviewWorkflowNew: React.FC = () => {
  const { currentOrganizationId } = useContext(AppContext);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams<{ id: string }>();

  const invalidateWorkflowsList = useCallback(() => {
    void queryClient.invalidateQueries('review-workflows');
  }, [queryClient]);

  const workflowQuery = useReviewWorkflow(id, currentOrganizationId, {
    enabled: !!id && !!currentOrganizationId,
    staleTime: 0,
  });
  const { mutate: saveAsDraft, isLoading: isSaving } = useUpdateReviewWorkflow();

  const [middleSteps, setMiddleSteps] = useState<WorkflowMiddleStep[]>([]);
  const [stepsInitialized, setStepsInitialized] = useState(false);
  const [selectedStepKey, setSelectedStepKey] = useState<WorkflowStepLocalKey | null>(null);
  const [reorderMode, setReorderMode] = useState(false);
  const [rightDrawerOpen, setRightDrawerOpen] = useState(false);
  const [defaultWorkflow, setDefaultWorkflow] = useState(false);
  const [showStepErrors, setShowStepErrors] = useState(false);

  const isStepInvalid = useCallback(
    (s: WorkflowMiddleStep) => !s.name?.trim() || !s.timeAllowed || !s.rejectAction || !s.timeToResubmit,
    []
  );

  const initialSnapshotRef = useRef<string | null>(null);

  useEffect(() => {
    if (workflowQuery.isFetching) {
      setStepsInitialized(false);
    }
  }, [workflowQuery.isFetching]);

  useEffect(() => {
    if (workflowQuery.data && !workflowQuery.isFetching && !stepsInitialized) {
      const sorted = [...workflowQuery.data.steps].sort((a, b) => a.stepNumber - b.stepNumber);
      const steps = sorted.length > 0
        ? sorted.map(mapApiStepToMiddleStep)
        : [createWorkflowMiddleStep({ name: t('performanceReporting.reviewWorkflows.new.steps.supervisorReview') })];
      setMiddleSteps(steps);
      initialSnapshotRef.current = snapshotState(steps, false);
      setStepsInitialized(true);
    }
  }, [workflowQuery.data, workflowQuery.isFetching, stepsInitialized]);

  const isDirty = stepsInitialized
    ? snapshotState(middleSteps, defaultWorkflow) !== initialSnapshotRef.current
    : false;

  const handleCloseNavigation = useDiscardWorkflowLeaveConfirm(isDirty, invalidateWorkflowsList);

  const handleSaveAsDraft = useCallback(() => {
    if (!workflowQuery.data) return;
    const hasInvalidStep = middleSteps.some(isStepInvalid);
    if (hasInvalidStep) {
      setShowStepErrors(true);
      enqueueSnackbar(t('performanceReporting.reviewWorkflows.new.snackbar.validationError'), {
        variant: 'error',
        autoHideDuration: 4000,
      });
      return;
    }
    saveAsDraft(
      {
        id: workflowQuery.data.reviewWorkflowId,
        organizationId: currentOrganizationId,
        data: {
          reviewWorkflowVersionId: workflowQuery.data.reviewWorkflowVersionId,
          name: workflowQuery.data.name,
          steps: middleSteps.map((step, index) => mapMiddleStepToApiStep(step, index + 1)),
        },
      },
      {
        onSuccess: () => {
          initialSnapshotRef.current = snapshotState(middleSteps, defaultWorkflow);
          enqueueSnackbar(t('performanceReporting.reviewWorkflows.new.snackbar.savedAsDraft'), {
            variant: 'success',
            autoHideDuration: 3000,
          });
          invalidateWorkflowsList();
          navigate('/admin/performance-reporting/review-workflows');
        },
        onError: (error) => {
          errorHelper.enqueueErrors(error as ServerErrorResponse | Error);
        },
      }
    );
  }, [workflowQuery.data, middleSteps, defaultWorkflow, saveAsDraft, navigate, isStepInvalid, currentOrganizationId, invalidateWorkflowsList]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor)
  );

  const selectedStep = useMemo(
    () => (selectedStepKey ? middleSteps.find((s) => s.localKey === selectedStepKey) : undefined),
    [middleSteps, selectedStepKey]
  );

  const pageTitle = t('performanceReporting.reviewWorkflows.new.pageTitle');
  const submittedLabel = t('performanceReporting.reviewWorkflows.new.steps.submitted');
  const approvedLabel = t('performanceReporting.reviewWorkflows.new.steps.approved');

  const openDrawerForStep = useCallback((key: WorkflowStepLocalKey) => {
    setSelectedStepKey(key);
    setRightDrawerOpen(true);
  }, []);

  const handleAddStep = useCallback(() => {
    const step = createWorkflowMiddleStep({
      name: t('performanceReporting.reviewWorkflows.new.steps.newStep'),
    });
    setMiddleSteps((prev) => [...prev, step]);
    openDrawerForStep(step.localKey);
  }, [openDrawerForStep]);

  const handleDeleteStep = useCallback((key: WorkflowStepLocalKey) => {
    setMiddleSteps((prev) => removeMiddleStepByKey(prev, key));
    setSelectedStepKey((cur) => {
      if (cur === key) {
        setRightDrawerOpen(false);
        return null;
      }
      return cur;
    });
  }, []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setMiddleSteps((prev) => reorderMiddleSteps(prev, String(active.id), String(over.id)));
  }, []);

  const handleStepPatch = useCallback((key: WorkflowStepLocalKey, patch: Partial<WorkflowMiddleStep>) => {
    setMiddleSteps((prev) => updateMiddleStep(prev, key, patch));
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setRightDrawerOpen(false);
    setSelectedStepKey(null);
  }, []);

  const sortableIds = useMemo(() => middleSteps.map((s) => s.localKey), [middleSteps]);

  return (
    <TakeoverLayout
      pageTitle={pageTitle}
      backNavIcon={BackNavIconVariant.Close}
      backNavAriaLabel={t('performanceReporting.reviewWorkflows.new.closeAriaLabel')}
      onBackClick={handleCloseNavigation}
      contentBackground='neutral'
      contentHeaderLeft={
        <Stack direction='row' alignItems='center' spacing={2} sx={{ minWidth: 0 }}>
          <Stack direction='row' alignItems='center' spacing={0.5} sx={{ minWidth: 0 }}>
            <Typography
              variant='subtitle1'
              sx={{
                fontWeight: 600,
                color: neutral[800],
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {workflowQuery.data?.name ?? ''}
            </Typography>
            <IconButton
              size='small'
              aria-label={t('performanceReporting.reviewWorkflows.new.workflowName.label')}
              sx={{ color: neutral[500] }}
            >
              <EditOutlinedIcon fontSize='small' />
            </IconButton>
          </Stack>
          <Chip
            label={t('performanceReporting.reviewWorkflows.new.statusBadge.draft')}
            size='small'
            sx={{ bgcolor: neutral[100], color: neutral[700], fontWeight: 500 }}
          />
        </Stack>
      }
      contentHeaderRight={
        <Stack direction='row' alignItems='center' spacing={2}>
          <Button
            variant='outlined'
            color='inherit'
            size='small'
            loading={isSaving}
            loadingPosition='end'
            disabled={!stepsInitialized}
            onClick={handleSaveAsDraft}
          >
            {t('performanceReporting.reviewWorkflows.new.actions.saveAsDraft')}
          </Button>
          <Button
            variant='contained'
            color='primary'
            size='small'
            disabled={!stepsInitialized}
          >
            {t('performanceReporting.reviewWorkflows.new.actions.publishWorkflow')}
          </Button>
        </Stack>
      }
      rightDrawerOpen={rightDrawerOpen}
      setRightDrawerOpen={setRightDrawerOpen}
      rightDrawer={{
        header: (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pr: 1 }}>
            <IconButton
              size='small'
              onClick={handleCloseDrawer}
              aria-label={t('performanceReporting.reviewWorkflows.new.editStep.closeAria')}
              sx={{
                color: 'text.secondary',
                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
              }}
            >
              <CloseIcon fontSize='small' />
            </IconButton>
            <Typography variant='h6' sx={{ fontWeight: 600, flex: 1 }}>
              {t('performanceReporting.reviewWorkflows.new.editStep.title')}
            </Typography>
          </Box>
        ),
        content:
          selectedStep && currentOrganizationId ? (
            <EditWorkflowStepForm
              key={selectedStep.localKey}
              step={selectedStep}
              organizationId={currentOrganizationId}
              onStepChange={(patch) => handleStepPatch(selectedStep.localKey, patch)}
            />
          ) : (
            <Box sx={{ p: 3 }}>
              <Typography variant='body2' color='text.secondary'>
                {t('performanceReporting.reviewWorkflows.new.editStep.selectStepHint')}
              </Typography>
            </Box>
          ),
        closeable: true,
        noPadding: true,
        verticalActions: rightDrawerOpen ? undefined : [],
      }}
    >
      {!stepsInitialized ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ width: '100%', maxWidth: 720, mx: 'auto' }}>
          <Card
            elevation={0}
            sx={{
              border: `1px solid ${neutral[200]}`,
              borderRadius: 2,
              bgcolor: neutral[25],
              boxShadow: 'none',
              overflow: 'hidden',
            }}
          >
            <Box
              component='header'
              sx={{
                bgcolor: neutral[50],
                borderBottom: `1px solid ${neutral[100]}`,
                px: 4,
                py: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
              }}
            >
              <Typography
                variant='subtitleLg'
                component='h2'
                sx={{ fontWeight: 600, color: neutral[500] }}
              >
                {t('performanceReporting.reviewWorkflows.new.steps.title')}
              </Typography>
              <Button
                variant='text'
                color='primary'
                size='small'
                onClick={() => setReorderMode((v) => !v)}
                aria-pressed={reorderMode}
              >
                {reorderMode
                  ? t('performanceReporting.reviewWorkflows.new.reorder.done')
                  : t('performanceReporting.reviewWorkflows.new.reorder.toggle')}
              </Button>
            </Box>
            <CardContent sx={{ bgcolor: neutral[25], pt: 3, px: 8, py: 4 }}>
              <Stack spacing={stepStackSpacing}>
                <Box sx={workflowStepListRowSx}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <TextField
                      fullWidth
                      variant='outlined'
                      value={submittedLabel}
                      InputProps={{ readOnly: true }}
                      inputProps={{ 'aria-label': submittedLabel }}
                      sx={readOnlyDashedTextFieldSx}
                    />
                  </Box>
                  <Box aria-hidden sx={workflowStepListFixedEndSpacerSx} />
                </Box>

                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
                    <Stack spacing={stepStackSpacing}>
                      {middleSteps.map((step) => (
                        <SortableWorkflowStepRow
                          key={step.localKey}
                          id={step.localKey}
                          title={step.name}
                          selected={selectedStepKey === step.localKey && rightDrawerOpen}
                          reorderMode={reorderMode}
                          hasError={showStepErrors && isStepInvalid(step)}
                          onSelect={(key) => openDrawerForStep(key)}
                          onDelete={handleDeleteStep}
                        />
                      ))}
                    </Stack>
                  </SortableContext>
                </DndContext>

                <Box sx={{ py: 0.5 }}>
                  <Link
                    component='button'
                    type='button'
                    variant='body2'
                    onClick={handleAddStep}
                    data-testid='workflow-add-step'
                    sx={{ fontWeight: 600, cursor: 'pointer' }}
                  >
                    {t('performanceReporting.reviewWorkflows.new.steps.addStep')}
                  </Link>
                </Box>

                <Box sx={workflowStepListRowSx}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <TextField
                      fullWidth
                      variant='outlined'
                      value={approvedLabel}
                      InputProps={{ readOnly: true }}
                      inputProps={{ 'aria-label': approvedLabel }}
                      sx={readOnlyDashedTextFieldSx}
                    />
                  </Box>
                  <Box aria-hidden sx={workflowStepListFixedEndSpacerSx} />
                </Box>

                <Divider sx={{ mt: 4, mb: 2 }} />

                <FormControlLabel
                  control={
                    <Switch
                      checked={defaultWorkflow}
                      onChange={(_e, checked) => setDefaultWorkflow(checked)}
                      color='primary'
                    />
                  }
                  label={
                    <Box>
                      <Typography variant='body2'>
                        {t('performanceReporting.reviewWorkflows.new.defaultWorkflow.label')}
                      </Typography>
                      <Typography variant='caption' color='text.secondary'>
                        {t('performanceReporting.reviewWorkflows.new.defaultWorkflow.helper')}
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start', ml: 0 }}
                />
              </Stack>
            </CardContent>
          </Card>
        </Box>
      )}
    </TakeoverLayout>
  );
};

export default ReviewWorkflowNew;
